// src/components/team/RecommendToDirectorModal.jsx
//
// Composer modal for the "Recommend Scrimly to your club director"
// flow. Coach fills in 4 fields (director name, director email, club
// name, current platform), reviews + tweaks a pre-filled email body,
// and clicks "Send via my email" — which opens their default mail
// client via `mailto:` with the email pre-composed.
//
// In parallel, the modal calls the submitClubLead Cloud Function to
// capture the lead server-side (so Kurt gets notified and can manually
// follow up even if the coach doesn't actually hit Send in their mail
// client — intent capture > literal send per BA D-2).
//
// Composer UI is English-only per BA D-7 (admin path; director is
// assumed English-comfortable).
//
// See features/recommend-to-director/02-wireframe.md §4.

import { useMemo, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '@/firebase/firebase';
import toast from 'react-hot-toast';

// Platform-specific anchor sentences injected into the pre-filled email
// body. Each is a single sentence positioned as a benefit-vs-competitor
// hook. Architecture §6.
const PLATFORM_ANCHORS = {
  byga:         "It does bilingual auto-translation, which Byga still doesn't do.",
  teamsnap:     "It actually translates parent messages — TeamSnap stores your roster; Scrimly runs your week.",
  sportsengine: "Worth a look — they're priced 50%+ under SportsEngine and do bilingual auto-translation that SE skips.",
  spond:        "It's like Spond but with auto-translation that Spond doesn't have.",
  nothing:      "It replaces the group chat + payment app + roster spreadsheet I've been juggling.",
  other:        "It pulls together messaging, registrations, and payment chases into one app, and it's bilingual without thinking.",
};

const PLATFORM_OPTIONS = [
  { value: 'byga',         label: 'Byga' },
  { value: 'teamsnap',     label: 'TeamSnap' },
  { value: 'sportsengine', label: 'SportsEngine' },
  { value: 'spond',        label: 'Spond' },
  { value: 'nothing',      label: 'Nothing (informal / spreadsheets)' },
  { value: 'other',        label: 'Something else' },
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function buildEmailBody({ directorName, coachName, teamName, clubName, currentPlatform }) {
  const anchor = PLATFORM_ANCHORS[currentPlatform] || PLATFORM_ANCHORS.other;
  // Marketing-site link added so the director has a self-serve landing
  // page. Points at the director-specific page (/clubs) rather than the
  // home page — the /clubs page is the director-targeted pitch with
  // bilingual + multi-team + Stripe-at-cost positioning + founding club
  // CTA all in one place. Plain-text URL — most mail clients auto-
  // linkify it.
  return `Hey ${directorName || 'there'},

Quick recommendation — I've been using a tool called Scrimly for ${teamName} this season. ${anchor} Worth a 15-min look: https://getscrimly.com/clubs

Want me to put you in touch with their founder? He's running a Founding Club program with the first 5 clubs at $199/mo, locked for life.

— ${coachName}`;
}

function buildSubject({ clubName }) {
  return `Have you seen Scrimly? Thinking about it for ${clubName || 'our club'}`;
}

export default function RecommendToDirectorModal({
  teamId,
  coachName,
  teamName,
  onClose,
  onSubmitted,
}) {
  const [directorName, setDirectorName] = useState('');
  const [directorEmail, setDirectorEmail] = useState('');
  const [clubName, setClubName] = useState('');
  const [currentPlatform, setCurrentPlatform] = useState('byga');
  const [subjectEdited, setSubjectEdited] = useState(false);
  const [subject, setSubject] = useState(buildSubject({ clubName: '' }));
  const [bodyEdited, setBodyEdited] = useState(false);
  const [body, setBody] = useState(
    buildEmailBody({ directorName: '', coachName, teamName, clubName: '', currentPlatform: 'byga' })
  );
  const [sending, setSending] = useState(false);

  // Live-recompute subject + body when their inputs change, but ONLY
  // if the coach hasn't manually edited them. Once they edit, freeze
  // — we don't want to clobber their changes when they switch the
  // platform select afterwards.
  const computedSubject = useMemo(
    () => buildSubject({ clubName }),
    [clubName]
  );
  const computedBody = useMemo(
    () => buildEmailBody({ directorName, coachName, teamName, clubName, currentPlatform }),
    [directorName, coachName, teamName, clubName, currentPlatform]
  );
  const effectiveSubject = subjectEdited ? subject : computedSubject;
  const effectiveBody    = bodyEdited    ? body    : computedBody;

  // Validation: all four fields non-empty + valid email.
  const directorEmailLower = directorEmail.trim().toLowerCase();
  const emailValid = EMAIL_RE.test(directorEmailLower);
  const canSend = !!directorName.trim()
    && emailValid
    && !!clubName.trim()
    && !!currentPlatform
    && !!effectiveSubject.trim()
    && !!effectiveBody.trim()
    && !sending;

  // Send: writes the lead server-side, then opens the coach's mail
  // client. We always open the mailto: regardless of callable success
  // — if the server write fails, the coach can still send the email
  // and we'll re-capture the lead via Kurt's manual follow-up.
  const handleSend = async () => {
    if (!canSend) return;
    setSending(true);

    const payload = {
      teamId,
      directorName: directorName.trim(),
      directorEmail: directorEmailLower,
      clubName: clubName.trim(),
      currentPlatform,
      subject: effectiveSubject.trim(),
      personalNote: effectiveBody.trim(),
    };

    // Call the callable. If it succeeds, great; if it fails with
    // resource-exhausted (rate limit), tell the coach but still open
    // their mail client so they can send.
    let callableOk = true;
    let callableErrorMsg = '';
    try {
      const callable = httpsCallable(functions, 'submitClubLead');
      const result = await callable(payload);
      // result.data.deduped is informational — we don't surface it
      // to the coach because that would leak "this director was
      // already recommended" info.
      callableOk = true;
      callableErrorMsg = result?.data?.deduped ? '' : '';
    } catch (err) {
      callableOk = false;
      // Capture the most-specific error message we can. Firebase
      // Functions v2 callable errors come back with `code` (e.g.
      // 'functions/internal') and `message`. Surfacing the code in
      // the console makes debugging much faster than just "INTERNAL".
      callableErrorMsg = err?.message || 'Could not save the lead.';
      // eslint-disable-next-line no-console
      console.warn('[RecommendToDirectorModal] submitClubLead failed', {
        code: err?.code,
        message: err?.message,
        details: err?.details,
      });
    }

    // Open the mailto: in the coach's default mail client. Use
    // encodeURIComponent on subject + body so special chars (`&`, `?`,
    // newlines) don't break the URL (security S4).
    const mailtoHref =
      `mailto:${encodeURIComponent(directorEmailLower)}`
      + `?subject=${encodeURIComponent(effectiveSubject.trim())}`
      + `&body=${encodeURIComponent(effectiveBody.trim())}`;
    // window.location.href is more reliable than window.open() for
    // mailto: (some browsers block popups for it).
    window.location.href = mailtoHref;

    setSending(false);

    if (callableOk) {
      toast.success(
        "Sent! Thanks for telling them. If they sign up, you get a perk on us.",
        { duration: 4000 }
      );
    } else {
      toast.error(
        "Couldn't save the lead, but mail opened in your client — you can send anyway.",
        { duration: 5000 }
      );
    }
    onSubmitted?.();
  };

  // Copy-to-clipboard fallback for coaches whose machine has no
  // default mail handler (Chrome → OS chain returns nothing), OR
  // coaches who prefer to paste into Gmail web / Outlook web / etc.
  // Composes the email as a formatted block so it pastes cleanly:
  //
  //   To: director@example.com
  //   Subject: Have you seen Scrimly?
  //
  //   Hey Robert,
  //   ...body...
  //
  // Most mail compose UIs don't parse the To/Subject headers
  // automatically (so the coach manually moves them), but having
  // them in the clipboard text means zero retyping.
  //
  // Also writes the lead doc server-side via submitClubLead (same
  // path as Send), so Kurt gets notified either way.
  const handleCopy = async () => {
    if (!canSend) return;
    setSending(true);

    const payload = {
      teamId,
      directorName: directorName.trim(),
      directorEmail: directorEmailLower,
      clubName: clubName.trim(),
      currentPlatform,
      subject: effectiveSubject.trim(),
      personalNote: effectiveBody.trim(),
    };

    // Fire-and-forget — lead capture mirrors the Send path. We don't
    // block the copy on the callable result; even if the callable
    // fails (rate limit etc.), the coach still gets the clipboard
    // content and can send manually.
    let callableOk = true;
    try {
      const callable = httpsCallable(functions, 'submitClubLead');
      await callable(payload);
    } catch (err) {
      callableOk = false;
      // eslint-disable-next-line no-console
      console.warn('[RecommendToDirectorModal] copy: submitClubLead failed', {
        code: err?.code,
        message: err?.message,
      });
    }

    const clipboardText =
      `To: ${directorEmailLower}\n`
      + `Subject: ${effectiveSubject.trim()}\n\n`
      + `${effectiveBody.trim()}\n`;

    let copyOk = false;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(clipboardText);
        copyOk = true;
      } else {
        // Very old browsers without the Clipboard API. Fall back to
        // a textarea + execCommand. Best-effort; modern Chrome,
        // Safari, Firefox, and the Capacitor Android WebView all
        // support navigator.clipboard.
        const ta = document.createElement('textarea');
        ta.value = clipboardText;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        copyOk = document.execCommand('copy');
        document.body.removeChild(ta);
      }
    } catch (err) {
      copyOk = false;
      // eslint-disable-next-line no-console
      console.warn('[RecommendToDirectorModal] clipboard write failed', err?.message);
    }

    setSending(false);

    if (copyOk) {
      const leadNote = callableOk ? '' : ' (lead save failed — that\'s okay)';
      toast.success(
        `Email copied! Paste into a new email to ${directorEmailLower}.${leadNote}`,
        { duration: 6000 }
      );
      onSubmitted?.();
    } else {
      toast.error(
        "Couldn't copy to clipboard. Select the message text below and copy manually.",
        { duration: 5000 }
      );
      // Don't dismiss the modal — coach can still read + manually copy
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-slate-900/50 px-3 py-6 sm:items-center"
      onClick={(e) => { if (e.target === e.currentTarget && !sending) onClose?.(); }}
    >
      <div className="relative w-full max-w-lg rounded-2xl bg-white dark:bg-zinc-900 shadow-2xl ring-1 ring-black/5 max-h-[calc(100vh-3rem)] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between gap-2 border-b border-zinc-200 dark:border-zinc-800 px-5 py-3.5 flex-shrink-0">
          <h2 className="text-sm font-bold text-zinc-900 dark:text-white">
            Recommend Scrimly to your club director
          </h2>
          <button
            type="button"
            onClick={() => !sending && onClose?.()}
            className="text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200 text-xl leading-none p-1 -m-1 min-w-[32px] min-h-[32px]"
            aria-label="Close"
          >
            &times;
          </button>
        </div>

        {/* Form */}
        <div className="overflow-y-auto px-5 py-4 space-y-3.5 flex-1">
          <Field label="Director's name">
            <input
              type="text"
              value={directorName}
              onChange={(e) => setDirectorName(e.target.value)}
              maxLength={200}
              autoFocus
              className={inputCls}
              placeholder="e.g. Robert Tornel"
            />
          </Field>

          <Field
            label="Director's email"
            error={directorEmail && !emailValid ? 'Looks like an invalid email.' : null}
          >
            <input
              type="email"
              value={directorEmail}
              onChange={(e) => setDirectorEmail(e.target.value)}
              maxLength={200}
              className={inputCls}
              placeholder="director@yourclub.com"
            />
          </Field>

          <Field label="Club name">
            <input
              type="text"
              value={clubName}
              onChange={(e) => setClubName(e.target.value)}
              maxLength={200}
              className={inputCls}
              placeholder="e.g. Fury FC"
            />
          </Field>

          <Field label="What does the club use now?">
            <select
              value={currentPlatform}
              onChange={(e) => setCurrentPlatform(e.target.value)}
              className={inputCls}
            >
              {PLATFORM_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </Field>

          <Field label="Subject">
            <input
              type="text"
              value={effectiveSubject}
              onChange={(e) => {
                setSubjectEdited(true);
                setSubject(e.target.value);
              }}
              maxLength={200}
              className={inputCls}
            />
          </Field>

          <Field label="Message (edit as you like)">
            <textarea
              value={effectiveBody}
              onChange={(e) => {
                setBodyEdited(true);
                setBody(e.target.value);
              }}
              maxLength={5000}
              rows={8}
              className={`${inputCls} font-normal leading-relaxed resize-y min-h-[10rem]`}
            />
          </Field>

          <p className="text-[11px] text-zinc-500 dark:text-zinc-400">
            <strong>Send via my email</strong> opens this in your default mail app.
            No default mail app? Use <strong>Copy email</strong> to paste it into
            Gmail, Outlook web, or anything else. Either way, we never send it from
            our servers — it goes from your account, to them.
          </p>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 border-t border-zinc-200 dark:border-zinc-800 px-5 py-3 flex-shrink-0 bg-zinc-50 dark:bg-zinc-900/50 flex-wrap">
          <button
            type="button"
            onClick={() => !sending && onClose?.()}
            className="text-xs font-semibold text-zinc-600 dark:text-zinc-300 hover:text-zinc-900 dark:hover:text-white px-3 py-2 min-h-[40px]"
          >
            Cancel
          </button>
          {/* Secondary CTA — copy-to-clipboard fallback for coaches whose
              machine has no default mail handler, OR who prefer Gmail/
              Outlook web. Same lead-capture path as Send. */}
          <button
            type="button"
            onClick={handleCopy}
            disabled={!canSend}
            className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 hover:bg-zinc-50 dark:hover:bg-zinc-700 disabled:opacity-50 disabled:cursor-not-allowed px-3 py-2 text-xs font-semibold text-zinc-700 dark:text-zinc-200 transition-colors min-h-[40px]"
          >
            Copy email
          </button>
          <button
            type="button"
            onClick={handleSend}
            disabled={!canSend}
            className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 disabled:bg-zinc-300 disabled:text-zinc-500 dark:disabled:bg-zinc-800 px-4 py-2 text-xs font-bold uppercase tracking-wide text-white transition-colors min-h-[40px]"
          >
            {sending ? 'Sending…' : 'Send via my email'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Small helper components kept local — used only here.

const inputCls =
  'w-full rounded-lg border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-950 px-3 py-2 text-sm text-zinc-900 dark:text-zinc-100 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 focus:outline-none placeholder:text-zinc-400 min-h-[40px]';

function Field({ label, error, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold text-zinc-700 dark:text-zinc-300 mb-1">
        {label}
      </span>
      {children}
      {error && (
        <span className="block text-[11px] text-red-600 dark:text-red-400 mt-1">
          {error}
        </span>
      )}
    </label>
  );
}
